import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';

import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStop,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { expVal, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	GO_HOME_SHORTCUT,
	GO_TO_RECENT_SHORTCUT,
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import { preloadRecentContentMenu } from '../../ContentMenu/preloadRecentContentMenu.ts';
import { preloadStarredContentMenu } from '../../ContentMenu/preloadStarredContentMenu.ts';
import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';

import { DiscoErrorBoundary } from '../components/DiscoErrorBoundary.tsx';
import { RecentContentMenuLoader } from './RecentContentMenuLoader.tsx';
import { RecentsEmptyStateLoader } from './RecentsEmptyStateLoader.tsx';
import { StarredContentMenuLoader } from './StarredContentMenuLoader.tsx';
import { useRecentItems, useRecentsFilterConfig } from './useRecentItems.tsx';
import { useStarredItems } from './useStarredItems.tsx';

const recentAttributes = { navVersion: '4', dropdownType: 'recent' };
const starredAttributes = { navVersion: '4', dropdownType: 'starred' };

const StopRecent = () => {
	return <ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={recentAttributes} />;
};

const StopStarred = () => (
	<ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={starredAttributes} />
);

type ForYouItemWrapperProps = {
	children: React.ReactNode;
	deprecatedKeyboardShortcut: string | string[] | undefined;
	homeUrl: string;
};

type GenericItemWrapperProps = {
	children: React.ReactNode;
	deprecatedKeyboardShortcut: string | string[] | undefined;
	keyboardShortcut: string | string[];
	onMouseEnter: () => void;
	onShortcutTriggered: () => void;
};

const ForYouItemWrapper = ({
	children,
	homeUrl,
	deprecatedKeyboardShortcut,
}: ForYouItemWrapperProps) => (
	<DiscoErrorBoundary>
		<KeyboardShortcutWrapper
			href={homeUrl}
			deprecatedKeyboardShortcut={deprecatedKeyboardShortcut}
			keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
			isAppNavigationShortcut
		>
			<HomeOnboardingTakeoverV2HomeNudge>
				<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
			</HomeOnboardingTakeoverV2HomeNudge>
		</KeyboardShortcutWrapper>
	</DiscoErrorBoundary>
);

const GenericItemWrapper = ({
	children,
	deprecatedKeyboardShortcut,
	keyboardShortcut,
	onMouseEnter,
	onShortcutTriggered,
}: GenericItemWrapperProps) => (
	<DiscoErrorBoundary>
		<div onMouseEnter={onMouseEnter} onFocus={onMouseEnter}>
			<KeyboardShortcutWrapper
				deprecatedKeyboardShortcut={deprecatedKeyboardShortcut}
				keyboardShortcut={keyboardShortcut}
				isAppNavigationShortcut
				onShortcutTriggered={onShortcutTriggered}
			>
				{children}
			</KeyboardShortcutWrapper>
		</div>
	</DiscoErrorBoundary>
);

export const useForYouConfig = (): ForYouConfig => {
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();
	const isHomeSelected = useRouteUrl(isOnHomeSelector);

	const isRecentOpenRef = useRef(false);
	const recentsFilterConfig = useRecentsFilterConfig();
	const recentViewAllUrl = HOME.toUrl({ view: 'recent' });
	const [isRecentOpen, setIsRecentOpen] = useState(false);
	const isRecentSelected = useRouteUrl(isOnRecentRoute);
	const onRecentMouseEnter = useCallback(() => {
		if (!isRecentOpenRef.current) {
			void RecentContentMenuLoader.preload();
			void preloadRecentContentMenu(cloudId);
		}
	}, [cloudId]);
	const onRecentShortcut = useCallback(() => {
		const next = !isRecentOpenRef.current;
		isRecentOpenRef.current = next;
		setIsRecentOpen(next);
		if (next) {
			isStarredOpenRef.current = false;
			setIsStarredOpen(false);
		}
	}, []);

	const isStarredOpenRef = useRef(false);
	const starredViewAllUrl = HOME.toUrl({ view: 'starred' });
	const [isStarredOpen, setIsStarredOpen] = useState(false);
	const isStarredSelected = useRouteUrl(isOnStarredRoute);
	const onStarredMouseEnter = useCallback(() => {
		if (!isStarredOpenRef.current) {
			void StarredContentMenuLoader.preload();
			void preloadStarredContentMenu();
		}
	}, []);
	const onStarredShortcut = useCallback(() => {
		const next = !isStarredOpenRef.current;
		isStarredOpenRef.current = next;
		setIsStarredOpen(next);
		if (next) {
			isRecentOpenRef.current = false;
			setIsRecentOpen(false);
		}
	}, []);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const startExperienceTracker = useCallback(
		(experienceName) => {
			experienceTracker.start({
				name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
				timeout: ExperienceTimeout.NAVIGATION_LOAD,
				attributes: {
					navVersion: '4',
					dropdownType: experienceName,
				},
			});
		},
		[experienceTracker],
	);

	const onRecentOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) startExperienceTracker('recent');
			isRecentOpenRef.current = isOpen;
			setIsRecentOpen(isOpen);
		},
		[startExperienceTracker],
	);
	const onStarredOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) startExperienceTracker('starred');
			isStarredOpenRef.current = isOpen;
			setIsStarredOpen(isOpen);
		},
		[startExperienceTracker],
	);

	const isKeyboardShortcutEnabled = !(isLiveEditMode || isOnEditRoute);

	const ForYouWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<ForYouItemWrapper
				{...props}
				homeUrl={homeUrl}
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_HOME_SHORTCUT : undefined}
			/>
		),
		[homeUrl, isKeyboardShortcutEnabled],
	);

	const RecentWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<GenericItemWrapper
				{...props}
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_TO_RECENT_SHORTCUT : undefined}
				keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
				onMouseEnter={onRecentMouseEnter}
				onShortcutTriggered={onRecentShortcut}
			/>
		),
		[isKeyboardShortcutEnabled, onRecentMouseEnter, onRecentShortcut],
	);

	const StarredWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<GenericItemWrapper
				{...props}
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_TO_STARRED_SHORTCUT : undefined}
				keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
				onMouseEnter={onStarredMouseEnter}
				onShortcutTriggered={onStarredShortcut}
			/>
		),
		[isKeyboardShortcutEnabled, onStarredMouseEnter, onStarredShortcut],
	);

	return useMemo<ForYouConfig>(
		() => ({
			forYouItem: {
				type: 'link' as const,
				menuItemId: 'yourWork',
				url: homeUrl,
				isSelected: isHomeSelected,
				ItemWrapper: ForYouWrapper,
			},
			recent: {
				menuItemId: 'recent',
				useItems: useRecentItems,
				viewAllUrl: recentViewAllUrl,
				hideActionsWhenEmpty: UNSAFE_expValNoExposure(
					'cc_mohiti_nav_empty_states',
					'isEnabled',
					false,
				),
				emptyElem: expVal('cc_mohiti_nav_empty_states', 'isEnabled', false) ? (
					<DiscoErrorBoundary>
						<RecentsEmptyStateLoader />
					</DiscoErrorBoundary>
				) : undefined,
				isSearchable: true,
				filterConfig: recentsFilterConfig,
				isSelected: isRecentSelected,
				isOpen: isRecentOpen,
				children: <RecentContentMenuLoader />,
				experienceEndTracker: StopRecent,
				onOpenChange: onRecentOpenChange,
				ItemWrapper: RecentWrapper,
			},
			starred: {
				menuItemId: 'starred',
				useItems: useStarredItems,
				viewAllUrl: starredViewAllUrl,
				hideActionsWhenEmpty: UNSAFE_expValNoExposure(
					'cc_mohiti_nav_empty_states',
					'isEnabled',
					false,
				),
				isSearchable: true,
				isOpen: isStarredOpen,
				isSelected: isStarredSelected,
				children: <StarredContentMenuLoader />,
				experienceEndTracker: StopStarred,
				onOpenChange: onStarredOpenChange,
				ItemWrapper: StarredWrapper,
			},
		}),
		[
			homeUrl,
			isHomeSelected,
			isRecentOpen,
			isRecentSelected,
			isStarredOpen,
			isStarredSelected,
			recentViewAllUrl,
			recentsFilterConfig,
			starredViewAllUrl,
			onRecentOpenChange,
			onStarredOpenChange,
			ForYouWrapper,
			RecentWrapper,
			StarredWrapper,
		],
	);
};

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};
const isOnStarredRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'starred' }),
};
const isOnRecentRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'recent' }),
};
